import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { HeadingItem } from '@/components/shared/headline/heading-item';
import { Box } from '@/components/shared/layout/box';
import { css } from '@/stitches.config';
import { IMy24Tracking } from '@/types/shared';

const CssAnimateInview = css({
  '.headline-slider--navigation, .headline-slider--heading > * > *': {
    opacity: 0,
    transform: 'translate(0, 30px)',
    transition:
      'opacity 0.6s cubic-bezier(0.33, 1, 0.68, 1), transform 0.6s cubic-bezier(0.33, 1, 0.68, 1)',
    willChange: 'transform, opacity',
  },
  variants: {
    inview: {
      true: {
        '.headline-slider--navigation, .headline-slider--heading > * > *': {
          opacity: 1,
          transform: 'translate(0, 0)',
          transition:
            'opacity 0.6s cubic-bezier(0.33, 1, 0.68, 1), transform 0.6s cubic-bezier(0.33, 1, 0.68, 1)',
          willChange: 'transform, opacity',
        },
        '.headline-slider--heading > * > *:nth-child(1)': {
          transitionDelay: '0.1s',
        },
        '.headline-slider--heading > * > *:nth-child(2)': {
          transitionDelay: '0.2s',
        },
        '.headline-slider--navigation': {
          transitionDelay: '0.3s',
        },
      },
    },
  },
});

interface HeadlineSliderProps {
  title: string;
  preTitle?: string;
  tracking?: IMy24Tracking;
  sectionName?: string;
  animateInview?: boolean;
  enableAnimateInview?: boolean;
  prevButtonClass: string;
  nextButtonClass: string;
}

export const HeadlineSlider = ({
  title = '',
  preTitle = '',
  tracking = 'lcv_headline',
  sectionName = '',
  animateInview = false,
  enableAnimateInview = false,
  prevButtonClass,
  nextButtonClass,
}: HeadlineSliderProps) => {
  return (
    <Box
      display="flex"
      justifyContent="between"
      alignItems="end"
      marginBottom="10"
      className={
        enableAnimateInview
          ? CssAnimateInview({ inview: animateInview })
          : undefined
      }
    >
      <HeadingItem
        title={title}
        lead={preTitle}
        isDarkTheme
        className="headline-slider--heading"
      />
      <Box
        display={{ '@initial': 'none', '@md': 'flex' }}
        columnGap="6"
        className="headline-slider--navigation"
        css={{
          '& > *.swiper-button-disabled': {
            '--bg-color': 'var(--colors-gray1100)',
            '--icon-color': 'var(--colors-gray900)',
            '& i': {
              color: '$gray900',
            },
          },
        }}
      >
        <ButtonIcon
          icon="chevron-left"
          size="lg"
          variant="button-icon-color-gray-dark"
          tracking={{
            dataTrack: tracking,
            dataTrackSection: sectionName,
            dataTrackValue: 'prev',
          }}
          className={prevButtonClass}
        />
        <ButtonIcon
          icon="chevron-right"
          size="lg"
          variant="button-icon-color-gray-dark"
          tracking={{
            dataTrack: tracking,
            dataTrackSection: sectionName,
            dataTrackValue: 'next',
          }}
          className={nextButtonClass}
        />
      </Box>
    </Box>
  );
};

HeadlineSlider.displayName = 'SharedEinsteinHeadlineSlider';
